import type { Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRaw,
  toRef,
  watch,
} from 'vue';

import type { ITreeItem } from '@components/tree/VTree/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { ITranslationsKeyMap } from '@modules/i18n/types';
import type { ITranslateManagerForm } from '@modules/translate-manager/types';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  useI18n,
  useLanguages,
} from '@modules/i18n/composables';
import { useI18nQuery } from '@modules/i18n/queries';
import {
  useFrontendLocalesStore,
  useI18nStore,
} from '@modules/i18n/store';
import { useToastStore } from '@modules/toast/store';
import { TRANSLATION_GROUP_TRANSLATE_MANAGER } from '@modules/translate-manager/constants';
import { useBackendTranslationsQuery } from '@modules/translate-manager/queries';
import {
  assert,
  isString,
} from '@utils/guards';

import { filterTranslationsList, prepareTranslationsList } from './utils';

export interface TranslateManagerPageComposable {
  isTranslationsFetching: Ref<boolean>;
  isBackendTranslationLoading: Ref<boolean>;
  selectedLanguage: Ref<string>;
  backendTranslations: Ref<ITreeItem[]>;
  frontendTranslations: Ref<ITreeItem[]>;
  unitedTreeTranslations: Ref<ITreeItem[]>;
  formSchema: Ref<FormSchema<ITranslateManagerForm>>;
  formValues: Ref<ITranslateManagerForm>;
  onSubmit(values: ITranslateManagerForm): void;
  onUploadLocalesFile(): Promise<void>;
}

export default function useTranslateManagerPage(): TranslateManagerPageComposable {
  const { t, i18next } = useTranslation();
  const { getTranslation } = useI18n();
  const { getTranslationsMaps } = useFrontendLocalesStore();
  const currentLocale = toRef(useI18nStore(), 'currentLocale');
  const { interfaceLanguages } = useLanguages();
  const { displayPresetToast } = useToastStore();

  const formValues = ref<ITranslateManagerForm>({
    language: currentLocale.value,
    search: '',
    onlyEmpty: false,
  });
  const selectedLanguage = computed(() => formValues.value.language);

  const { refetch, isFetching: isTranslationsFetching } = useI18nQuery(selectedLanguage);
  const { data: backendTranslationsMap, isLoading: isBackendTranslationLoading } = useBackendTranslationsQuery();

  const frontendTranslationsMap = getTranslationsMaps();
  const frontendTranslationsKeyMap = computed(() => {
    const result: ITranslationsKeyMap = {};

    const addResultPath = (part: string, keyMap: ITranslationsKeyMap): ITranslationsKeyMap => {
      if (!keyMap[part]) {
        keyMap[part] = {};
      }

      const keyMapPart = keyMap[part];
      assert(!isString(keyMapPart));

      return keyMapPart;
    };

    for (const key of Object.getOwnPropertySymbols(frontendTranslationsMap)) {
      const map = frontendTranslationsMap[key];
      for (const item of Object.values(map.value)) {
        const parts = item.split('.');
        let partResult = result;
        for (const [id, part] of parts.entries()) {
          if (id === parts.length - 1) {
            partResult[part] = '';
          } else {
            partResult = addResultPath(part, partResult);
          }
        }
      }
    }
    return result;
  });

  const backendTranslations = computed<ITreeItem[]>(() => (
    filterTranslationsList(
      prepareTranslationsList(backendTranslationsMap.value),
      t,
      formValues.value,
    )
  ));
  const frontendTranslations = computed<ITreeItem[]>(() => (
    filterTranslationsList(
      prepareTranslationsList(frontendTranslationsKeyMap.value),
      t,
      formValues.value,
    )
  ));

  const unitedTreeTranslations = computed<ITreeItem[]>(() => [...frontendTranslations.value, ...backendTranslations.value]);

  async function onNewLangUploaded(): Promise<void> {
    const currentLanguage = selectedLanguage.value;
    const { data } = await refetch();
    if (data) {
      i18next.addResourceBundle(currentLanguage, 'default', toRaw(data));
    }
  }

  const formSchema = computed<FormSchema<ITranslateManagerForm>>(() => ({
    language: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'LANGUAGE').value,
      required: true,
      value: formValues.value.language,
      options: interfaceLanguages.value,
    },
    search: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'SEARCH_LOCALES').value,
      value: formValues.value.search,
      disabled: formValues.value.onlyEmpty,
      iconLeft: 'pi-search',
      allowClear: true,
    },
    onlyEmpty: {
      type: EFormInputTypes.CHECKBOX,
      label: getTranslation(TRANSLATION_GROUP_TRANSLATE_MANAGER, 'EMPTY_LOCALES').value,
      value: formValues.value.onlyEmpty,
    },
  }));

  function onSubmit(values: ITranslateManagerForm): void {
    formValues.value = values;
  }

  async function onUploadLocalesFile(): Promise<void> {
    try {
      await onNewLangUploaded();
      displayPresetToast('success');
    } catch {}
  }

  function refetchAfterChange(): void {
    void onNewLangUploaded();
  }

  watch(selectedLanguage, onNewLangUploaded);

  onMounted(() => {
    i18next.on('changedResources', refetchAfterChange);
  });

  onUnmounted(() => {
    i18next.off('changedResources', refetchAfterChange);
  });

  return {
    isTranslationsFetching,
    isBackendTranslationLoading,
    formSchema,
    formValues,
    backendTranslations,
    frontendTranslations,
    unitedTreeTranslations,
    selectedLanguage,
    onUploadLocalesFile,
    onSubmit,
  };
}
